/**
 * Annotation State Management using Zustand
 */
"use client";

import { create } from "zustand";
import type { Annotation, AnnotationStyle, AnnotationColor } from "./types";
import { db } from "./database";

interface AnnotationState {
  annotations: Map<string, Annotation>;
  selectedId: string | null;
  isEnabled: boolean;
  activeStyle: AnnotationStyle;
  activeColor: AnnotationColor;

  // Actions
  addAnnotation: (annotation: Annotation) => Promise<void>;
  removeAnnotation: (id: string) => Promise<void>;
  updateAnnotation: (id: string, updates: Partial<Annotation>) => Promise<void>;
  selectAnnotation: (id: string | null) => void;
  setEnabled: (enabled: boolean) => void;
  setActiveStyle: (style: AnnotationStyle) => void;
  setActiveColor: (color: AnnotationColor) => void;
  loadAnnotations: (pageUrl: string) => Promise<void>;
  clearAll: () => Promise<void>;
}

export const useAnnotationStore = create<AnnotationState>((set, get) => ({
  annotations: new Map(),
  selectedId: null,
  isEnabled: false,
  activeStyle: "highlight",
  activeColor: "yellow",

  addAnnotation: async (annotation) => {
    await db.saveAnnotation(annotation);
    set((state) => ({
      annotations: new Map(state.annotations).set(annotation.id, annotation),
    }));
  },

  removeAnnotation: async (id) => {
    await db.deleteAnnotation(id);
    set((state) => {
      const newMap = new Map(state.annotations);
      newMap.delete(id);
      return {
        annotations: newMap,
        selectedId: state.selectedId === id ? null : state.selectedId,
      };
    });
  },

  updateAnnotation: async (id, updates) => {
    const annotation = get().annotations.get(id);
    if (!annotation) return;

    const updated = { ...annotation, ...updates, updated: Date.now() };
    await db.saveAnnotation(updated);
    set((state) => ({
      annotations: new Map(state.annotations).set(id, updated),
    }));
  },

  selectAnnotation: (id) => {
    set({ selectedId: id });
  },

  setEnabled: (enabled) => {
    set({ isEnabled: enabled });
  },

  setActiveStyle: (style) => {
    set({ activeStyle: style });
  },

  setActiveColor: (color) => {
    set({ activeColor: color });
  },

  loadAnnotations: async (pageUrl) => {
    const annotations = await db.getAnnotationsByPage(pageUrl);
    const annotationMap = new Map<string, Annotation>(
      annotations.map((a: Annotation) => [a.id, a])
    );
    set({ annotations: annotationMap });
  },

  clearAll: async () => {
    const annotations = get().annotations;
    for (const id of annotations.keys()) {
      await db.deleteAnnotation(id);
    }
    set({ annotations: new Map(), selectedId: null });
  },
}));
